import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { searchUsers } from '../services/searchApi';
import LeagueBadge from '../components/league/LeagueBadge';

function SearchUsers() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      return undefined;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const data = await searchUsers(query.trim());
        setResults(data.users);
        setHasSearched(true);
      } catch {
        setResults([]);
        setHasSearched(true);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-puzzle-text mb-2">Find Players</h1>
          <p className="text-puzzle-text-secondary">Search by username</p>
        </div>

        {/* Search Input */}
        <div className="mb-6">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-puzzle-text-secondary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search usernames..."
              className="w-full pl-10 pr-4 py-3 bg-puzzle-surface border border-puzzle-border rounded-lg text-puzzle-text placeholder-puzzle-text-secondary focus:outline-none focus:ring-2 focus:ring-puzzle-blue focus:border-transparent"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-puzzle-blue" />
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="bg-puzzle-surface rounded-lg shadow overflow-hidden">
          {results.length > 0 && (
            <div className="divide-y divide-puzzle-border">
              {results.map((u) => (
                <Link
                  key={u.id}
                  to={`/profile/${u.username}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-puzzle-hover transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-puzzle-muted overflow-hidden flex items-center justify-center flex-shrink-0">
                    {u.avatar ? (
                      <img
                        src={u.avatar}
                        alt={u.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-puzzle-text-secondary">
                        {u.username.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-puzzle-text truncate">
                      {u.username}
                    </div>
                    {u.displayName && (
                      <div className="text-sm text-puzzle-text-secondary truncate">
                        {u.displayName}
                      </div>
                    )}
                  </div>
                  {u.leagueTier && (
                    <LeagueBadge tier={u.leagueTier} size="sm" />
                  )}
                </Link>
              ))}
            </div>
          )}
          {results.length === 0 && hasSearched && !isLoading && (
            <div className="text-center py-12 px-4">
              <p className="text-puzzle-text-secondary">
                No users found matching &quot;
                {query}
                &quot;
              </p>
            </div>
          )}
          {results.length === 0 && !hasSearched && query.length < 2 && (
            <div className="text-center py-12 px-4">
              <p className="text-puzzle-text-secondary">
                Type at least 2 characters to search
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchUsers;
